import { useState, useEffect } from 'react';
import api from '../../api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Crown, Users, Loader2, Search, Plus, Trash2, Edit, Gift, Percent, Star, Key, Copy, Check, Calendar, RefreshCw } from 'lucide-react';
import Pagination from '../../components/Pagination';

const formatMoney = (amount) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount);
const formatDate = (date) => new Date(date).toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric' });

export default function AdminVIP() {
  const [vipPlans, setVipPlans] = useState([]);
  const [vipUsers, setVipUsers] = useState([]);
  const [vipKeys, setVipKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('plans');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [generatedKeys, setGeneratedKeys] = useState([]);
  const itemsPerPage = 10;

  const [planForm, setPlanForm] = useState({
    name: '',
    price: '',
    duration_days: 30,
    discount_percent: 0,
    benefits: '',
    is_active: true
  });

  const [assignForm, setAssignForm] = useState({
    user_id: '',
    plan_id: '',
    duration_days: 30
  });

  const [keyGenForm, setKeyGenForm] = useState({
    plan_id: '',
    quantity: 1
  });

  useEffect(() => { 
    fetchVipData(); 
  }, []);

  const fetchVipData = async () => {
    setIsLoading(true);
    const [plansRes, usersRes, allUsersRes, keysRes] = await Promise.allSettled([
      api.get('/admin/vip/plans'),
      api.get('/admin/vip/users'),
      api.get('/admin/users'),
      api.get('/admin/vip/keys')
    ]);
    const pick = (res, fallback) => (res.status === 'fulfilled' ? res.value.data : fallback);
    if (plansRes.status === 'rejected') toast.error('Could not load VIP plans');
    if (keysRes.status === 'rejected') toast.error('Could not load VIP keys');
    setVipPlans(pick(plansRes, []));
    setVipUsers(pick(usersRes, []));
    const all = pick(allUsersRes, []);
    setAllUsers(all.data || all);
    setVipKeys(pick(keysRes, []));
    setIsLoading(false);
  };

  const handleSavePlan = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...planForm,
        price: parseFloat(planForm.price),
        discount_percent: parseFloat(planForm.discount_percent),
        benefits: planForm.benefits.split('\n').filter(b => b.trim())
      };
      
      if (editingPlan) {
        await api.put(`/admin/vip/plans/${editingPlan.id}`, payload);
        toast.success('VIP plan updated');
      } else {
        await api.post('/admin/vip/plans', payload);
        toast.success('VIP plan created');
      }
      setShowPlanDialog(false);
      resetPlanForm();
      fetchVipData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this VIP plan?')) return;
    try {
      await api.delete(`/admin/vip/plans/${planId}`);
      toast.success('VIP plan deleted');
      fetchVipData();
    } catch (error) {
      toast.error('Failed to delete plan');
    }
  };

  const handleAssignVIP = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/admin/vip/assign', assignForm);
      toast.success('VIP status assigned');
      setShowAssignDialog(false);
      setAssignForm({ user_id: '', plan_id: '', duration_days: 30 });
      fetchVipData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign VIP');
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeVIP = async (userId) => {
    if (!window.confirm('Are you sure you want to revoke VIP status?')) return;
    try {
      await api.post(`/admin/vip/revoke/${userId}`);
      toast.success('VIP status revoked');
      fetchVipData();
    } catch (error) {
      toast.error('Failed to revoke VIP');
    }
  };

  const handleGenerateKeys = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await api.post('/admin/vip/keys/generate', keyGenForm);
      setGeneratedKeys(response.data.keys);
      toast.success(`Generated ${response.data.keys.length} VIP keys`);
      fetchVipData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate keys');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!window.confirm('Delete this VIP key?')) return;
    try {
      await api.delete(`/admin/vip/keys/${keyId}`);
      toast.success('Key deleted');
      fetchVipData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete key');
    }
  };

  const copyToClipboard = async (key) => {
    await navigator.clipboard.writeText(key);
    setCopiedKey(key);
    toast.success('Key copied!');
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const copyAllKeys = async () => {
    const keysText = generatedKeys.join('\n');
    await navigator.clipboard.writeText(keysText);
    toast.success('All keys copied!');
  };

  const resetPlanForm = () => {
    setPlanForm({ name: '', price: '', duration_days: 30, discount_percent: 0, benefits: '', is_active: true });
    setEditingPlan(null);
  };

  const openEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      name: plan.name,
      price: plan.price.toString(),
      duration_days: plan.duration_days,
      discount_percent: plan.discount_percent,
      benefits: plan.benefits?.join('\n') || '',
      is_active: plan.is_active
    });
    setShowPlanDialog(true);
  };

  // Filter VIP users
  const filteredUsers = vipUsers.filter(u => 
    u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter VIP keys
  const filteredKeys = vipKeys.filter(k =>
    k.key?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    k.plan_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    k.redeemed_by_username?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = activeTab === 'users' 
    ? Math.ceil(filteredUsers.length / itemsPerPage)
    : Math.ceil(filteredKeys.length / itemsPerPage);
  
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const paginatedKeys = filteredKeys.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const unusedKeysCount = vipKeys.filter(k => !k.is_used).length;
  const usedKeysCount = vipKeys.filter(k => k.is_used).length;

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#FFD700]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Outfit'] flex items-center gap-2">
            <Crown className="w-7 h-7 text-[#FFD700]" />
            VIP Management
          </h1>
          <p className="text-zinc-500">Manage VIP plans, members, and keys</p>
        </div>
        <Button data-testid="vip-refresh-btn" variant="outline" size="sm" onClick={fetchVipData} className="border-white/10 text-zinc-300">
          <RefreshCw className="w-4 h-4 mr-2" />Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[#0A0A0A] border-[#FFD700]/30">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-[#FFD700]/20 flex items-center justify-center">
              <Crown className="w-6 h-6 text-[#FFD700]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#FFD700]">{vipUsers.length}</p>
              <p className="text-sm text-zinc-500">VIP Members</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#0A0A0A] border-purple-500/30">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Star className="w-6 h-6 text-purple-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-500">{vipPlans.length}</p>
              <p className="text-sm text-zinc-500">VIP Plans</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#0A0A0A] border-green-500/30">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Key className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-500">{unusedKeysCount}</p>
              <p className="text-sm text-zinc-500">Available Keys</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#0A0A0A] border-blue-500/30">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Check className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-500">{usedKeysCount}</p>
              <p className="text-sm text-zinc-500">Redeemed Keys</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-2 overflow-x-auto">
        <Button
          variant={activeTab === 'plans' ? 'default' : 'ghost'}
          data-testid="vip-tab-plans"
          onClick={() => { setActiveTab('plans'); setCurrentPage(1); }}
          className={activeTab === 'plans' ? 'bg-[#FFD700] text-black' : ''}
        >
          <Gift className="w-4 h-4 mr-2" />VIP Plans
        </Button>
        <Button
          variant={activeTab === 'keys' ? 'default' : 'ghost'}
          data-testid="vip-tab-keys"
          onClick={() => { setActiveTab('keys'); setCurrentPage(1); }}
          className={activeTab === 'keys' ? 'bg-[#FFD700] text-black' : ''}
        >
          <Key className="w-4 h-4 mr-2" />VIP Keys ({vipKeys.length})
        </Button>
        <Button
          variant={activeTab === 'users' ? 'default' : 'ghost'}
          onClick={() => { setActiveTab('users'); setCurrentPage(1); }}
          className={activeTab === 'users' ? 'bg-[#FFD700] text-black' : ''}
        >
          <Users className="w-4 h-4 mr-2" />VIP Members ({vipUsers.length})
        </Button>
      </div>

      {/* Plans Tab */}
      {activeTab === 'plans' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={showPlanDialog} onOpenChange={(open) => { setShowPlanDialog(open); if (!open) resetPlanForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="create-vip-plan-btn" className="bg-[#FFD700] text-black hover:bg-[#E5C100]">
                  <Plus className="w-4 h-4 mr-2" />Create VIP Plan
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0A0A0A] border-2 border-[#FFD700]/30 max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-white">{editingPlan ? 'Edit VIP Plan' : 'Create VIP Plan'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSavePlan} className="space-y-4">
                  <div>
                    <Label className="text-[#FFD700] text-xs uppercase">Plan Name</Label>
                    <Input
                      data-testid="vip-plan-name-input"
                      value={planForm.name}
                      onChange={(e) => setPlanForm({...planForm, name: e.target.value})}
                      placeholder="e.g., Trial, 1 Month, 3 Months"
                      className="bg-[#121212] border-white/10 text-white"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#FFD700] text-xs uppercase">Price (₦)</Label>
                      <Input
                        type="number"
                        data-testid="vip-plan-price-input"
                        value={planForm.price}
                        onChange={(e) => setPlanForm({...planForm, price: e.target.value})}
                        placeholder="5000"
                        className="bg-[#121212] border-white/10 text-white"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-[#FFD700] text-xs uppercase">Duration (Days)</Label>
                      <Input
                        type="number"
                        value={planForm.duration_days}
                        onChange={(e) => setPlanForm({...planForm, duration_days: parseInt(e.target.value)})}
                        className="bg-[#121212] border-white/10 text-white"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#FFD700] text-xs uppercase">Discount Percent (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={planForm.discount_percent}
                      onChange={(e) => setPlanForm({...planForm, discount_percent: e.target.value})}
                      placeholder="10"
                      className="bg-[#121212] border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-[#FFD700] text-xs uppercase">Benefits (one per line)</Label>
                    <textarea
                      value={planForm.benefits}
                      onChange={(e) => setPlanForm({...planForm, benefits: e.target.value})}
                      placeholder="Priority support&#10;Exclusive products&#10;Special discounts"
                      className="w-full h-24 px-3 py-2 rounded-md bg-[#121212] border border-white/10 text-white resize-none"
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-[#121212] rounded-lg">
                    <span className="text-white">Active</span>
                    <Switch
                      checked={planForm.is_active}
                      onCheckedChange={(checked) => setPlanForm({...planForm, is_active: checked})}
                    />
                  </div>
                  <Button data-testid="vip-plan-submit-btn" type="submit" disabled={saving} className="w-full bg-[#FFD700] text-black">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {editingPlan ? 'Update Plan' : 'Create Plan'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {vipPlans.length === 0 ? (
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-12 text-center">
                <Crown className="w-16 h-16 mx-auto text-zinc-600 mb-4" />
                <p className="text-zinc-400">No VIP plans created yet</p>
                <p className="text-zinc-500 text-sm mt-2">Create Trial (7 days), 1 Month, and 3 Month plans</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {vipPlans.map((plan) => (
                <Card key={plan.id} data-testid={`vip-plan-card-${plan.id}`} className="bg-[#0A0A0A] border-2 border-[#FFD700]/30 relative overflow-hidden">
                  {!plan.is_active && (
                    <div className="absolute top-0 right-0 bg-red-500 text-white text-xs px-2 py-1">Inactive</div>
                  )}
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-[#FFD700]">
                      <Crown className="w-5 h-5" />
                      {plan.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-white">{formatMoney(plan.price)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-zinc-400">
                      <Calendar className="w-4 h-4" />
                      <span>{plan.duration_days} days</span>
                    </div>
                    
                    {plan.discount_percent > 0 && (
                      <Badge className="bg-green-500/20 text-green-500">
                        {plan.discount_percent}% Discount on all products
                      </Badge>
                    )}
                    
                    {plan.benefits?.length > 0 && (
                      <ul className="space-y-1">
                        {plan.benefits.slice(0, 3).map((benefit, idx) => (
                          <li key={idx} className="text-sm text-zinc-400 flex items-center gap-2">
                            <Star className="w-3 h-3 text-[#FFD700]" />{benefit}
                          </li>
                        ))}
                      </ul>
                    )}
                    
                    <div className="flex gap-2 pt-2">
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => openEditPlan(plan)}>
                        <Edit className="w-4 h-4 mr-1" />Edit
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-500 border-red-500/30" onClick={() => handleDeletePlan(plan.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Keys Tab */}
      {activeTab === 'keys' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Search keys..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="pl-10 bg-[#121212] border-white/10 text-white"
              />
            </div>
            
            <Dialog open={showKeyDialog} onOpenChange={(open) => { setShowKeyDialog(open); if (!open) { setGeneratedKeys([]); setKeyGenForm({ plan_id: '', quantity: 1 }); } }}>
              <DialogTrigger asChild>
                <Button data-testid="generate-vip-keys-btn" className="bg-[#FFD700] text-black hover:bg-[#E5C100]">
                  <Key className="w-4 h-4 mr-2" />Generate Keys
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0A0A0A] border-2 border-[#FFD700]/30">
                <DialogHeader>
                  <DialogTitle className="text-white">Generate VIP Keys</DialogTitle>
                </DialogHeader>
                
                {generatedKeys.length > 0 ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-green-500 font-medium">Generated {generatedKeys.length} keys!</span>
                      <Button size="sm" variant="outline" onClick={copyAllKeys}>
                        <Copy className="w-4 h-4 mr-1" />Copy All
                      </Button>
                    </div>
                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {generatedKeys.map((key, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-[#121212] rounded-md">
                          <code className="text-[#FFD700] font-mono text-sm">{key}</code>
                          <Button size="sm" variant="ghost" onClick={() => copyToClipboard(key)}>
                            {copiedKey === key ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                          </Button>
                        </div>
                      ))}
                    </div>
                    <Button onClick={() => { setGeneratedKeys([]); }} className="w-full" variant="outline">
                      Generate More
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleGenerateKeys} className="space-y-4">
                    <div>
                      <Label className="text-[#FFD700] text-xs uppercase">Select VIP Plan</Label>
                      <select
                        data-testid="vip-key-plan-select"
                        value={keyGenForm.plan_id}
                        onChange={(e) => setKeyGenForm({...keyGenForm, plan_id: e.target.value})}
                        className="w-full px-3 py-2 rounded-md bg-[#121212] border border-white/10 text-white"
                        required
                      >
                        <option value="">Choose a plan...</option>
                        {vipPlans.map(plan => (
                          <option key={plan.id} value={plan.id}>{plan.name} - {plan.duration_days} days - {formatMoney(plan.price)}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-[#FFD700] text-xs uppercase">Quantity (1-100)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        value={keyGenForm.quantity}
                        onChange={(e) => setKeyGenForm({...keyGenForm, quantity: parseInt(e.target.value) || 1})}
                        className="bg-[#121212] border-white/10 text-white"
                        required
                      />
                    </div>
                    <Button data-testid="vip-key-generate-submit" type="submit" disabled={saving || !keyGenForm.plan_id} className="w-full bg-[#FFD700] text-black">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />}
                      Generate Keys
                    </Button>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </div>

          {filteredKeys.length === 0 ? (
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-12 text-center">
                <Key className="w-16 h-16 mx-auto text-zinc-600 mb-4" />
                <p className="text-zinc-400">No VIP keys generated yet</p>
                <p className="text-zinc-500 text-sm mt-2">Generate keys for users to redeem</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="space-y-2">
                {paginatedKeys.map((key) => (
                  <Card key={key.id} data-testid={`vip-key-card-${key.id}`} className={`bg-[#0A0A0A] border-2 ${key.is_used ? 'border-zinc-700' : 'border-green-500/30'}`}>
                    <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${key.is_used ? 'bg-zinc-700/50' : 'bg-green-500/20'}`}>
                          <Key className={`w-5 h-5 ${key.is_used ? 'text-zinc-500' : 'text-green-500'}`} />
                        </div>
                        <div>
                          <code className={`font-mono ${key.is_used ? 'text-zinc-500' : 'text-[#FFD700]'}`}>{key.key}</code>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={key.is_used ? 'bg-zinc-700 text-zinc-400' : 'bg-green-500/20 text-green-500'}>
                              {key.is_used ? 'Used' : 'Available'}
                            </Badge>
                            <span className="text-xs text-zinc-500">{key.plan_name} • {key.duration_days} days</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {key.is_used ? (
                          <span className="text-xs text-zinc-500">
                            Redeemed by {key.redeemed_by_username} on {formatDate(key.redeemed_at)}
                          </span>
                        ) : (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => copyToClipboard(key.key)}>
                              {copiedKey === key.key ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteKey(key.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              )}
            </>
          )}
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Search VIP members..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="pl-10 bg-[#121212] border-white/10 text-white"
              />
            </div>
            
            <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
              <DialogTrigger asChild>
                <Button className="bg-[#FFD700] text-black hover:bg-[#E5C100]">
                  <Plus className="w-4 h-4 mr-2" />Assign VIP
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0A0A0A] border-2 border-[#FFD700]/30">
                <DialogHeader>
                  <DialogTitle className="text-white">Assign VIP Status</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAssignVIP} className="space-y-4">
                  <div>
                    <Label className="text-[#FFD700] text-xs uppercase">Select User</Label>
                    <select
                      value={assignForm.user_id}
                      onChange={(e) => setAssignForm({...assignForm, user_id: e.target.value})}
                      className="w-full px-3 py-2 rounded-md bg-[#121212] border border-white/10 text-white"
                      required
                    >
                      <option value="">Choose a user...</option>
                      {allUsers.filter(u => !u.is_vip).map(user => (
                        <option key={user.id} value={user.id}>{user.username} ({user.email})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-[#FFD700] text-xs uppercase">Select VIP Plan</Label>
                    <select
                      value={assignForm.plan_id}
                      onChange={(e) => setAssignForm({...assignForm, plan_id: e.target.value})}
                      className="w-full px-3 py-2 rounded-md bg-[#121212] border border-white/10 text-white"
                      required
                    >
                      <option value="">Choose a plan...</option>
                      {vipPlans.filter(p => p.is_active).map(plan => (
                        <option key={plan.id} value={plan.id}>{plan.name} - {formatMoney(plan.price)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-[#FFD700] text-xs uppercase">Duration (Days)</Label>
                    <Input
                      type="number"
                      value={assignForm.duration_days}
                      onChange={(e) => setAssignForm({...assignForm, duration_days: parseInt(e.target.value)})}
                      className="bg-[#121212] border-white/10 text-white"
                      required
                    />
                  </div>
                  <Button type="submit" disabled={saving} className="w-full bg-[#FFD700] text-black">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Crown className="w-4 h-4 mr-2" />}
                    Assign VIP Status
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {filteredUsers.length === 0 ? (
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-12 text-center">
                <Users className="w-16 h-16 mx-auto text-zinc-600 mb-4" />
                <p className="text-zinc-400">No VIP members found</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="space-y-2">
                {paginatedUsers.map((user) => (
                  <Card key={user.id} className="bg-[#0A0A0A] border-white/10">
                    <CardContent className="p-4 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-[#FFD700]/20 flex items-center justify-center">
                          <Crown className="w-6 h-6 text-[#FFD700]" />
                        </div>
                        <div>
                          <p className="font-semibold text-white">{user.username}</p>
                          <p className="text-sm text-zinc-500">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <Badge className="bg-[#FFD700]/20 text-[#FFD700]">{user.vip_plan_name}</Badge>
                          <p className="text-xs text-zinc-500 mt-1">Expires: {formatDate(user.vip_expires_at)}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-500 border-red-500/30"
                          onClick={() => handleRevokeVIP(user.id)}
                        >
                          Revoke
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
